import { type IExceptionTelemetry, SeverityLevel } from "@microsoft/applicationinsights-web";
import { isNil } from "lodash";

export interface DropdownItem {
  text: string;
  value: string | number;
  disabled?: boolean;
}

export interface NormalizedDropdown<T extends string | number = string> {
  items: (number | DropdownItem)[];
  selected: T[];
}

function logError(errorMessage: string, errorStack?: string | null) {
  const error = new Error(errorMessage);
  console.error(error);
  errorStack && console.log(errorStack);
  if (window.appInsights) {
    const appInsightsException: IExceptionTelemetry = { error, severityLevel: SeverityLevel.Error };
    window.appInsights.trackException(appInsightsException);
  }
}

export const unlimitedNumber = 2147483647;

const utils = {
  getItemsPageRange: (activePage: number, itemsPerPageAmount: number, itemsAmount: number) => {
    let loadedItemsAmount = itemsPerPageAmount * (activePage - 1) + 1;
    if (activePage < 1) {
      loadedItemsAmount = 0;
      logError("List view. Items page range exception, negative active page");
    }
    let from = itemsAmount === 0 ? 0 : loadedItemsAmount;

    let to = from + itemsPerPageAmount - 1;
    to = to > itemsAmount ? itemsAmount : to;

    return { from, to };
  },

  normalizeDropdownItems: <T extends string | number>(items: T[]): NormalizedDropdown<T> => {
    return {
      items: items.map((item) => {
        if (typeof item === "string") {
          return {
            text: item,
            value: item,
          };
        }
        return item as number; // NOSONAR
      }),
      selected: items,
    };
  },

  getEmptyMultiSelectItems(): NormalizedDropdown {
    return { items: [], selected: [] };
  },

  setPageTitle(title?: string) {
    const newTitle = `${title}`;
    if (title && document.title !== newTitle) {
      document.title = newTitle;
    }
  },

  isUnlimited(number?: number) {
    return isNil(number) || number >= unlimitedNumber;
  },

  unlimitedNumber(number?: number) {
    return utils.isUnlimited(number) ? "Unlimited" : number!.toLocaleString("en-US");
  },

  logError,
};

export default utils;
